import { useEffect, useRef, useState, type FC } from 'react';
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  VideoIcon,
  FileTextIcon,
  SearchIcon,
  ListIcon,
  ScissorsIcon,
  PlayIcon,
  ClockIcon,
  ClipboardCheckIcon,
  LightbulbIcon,
  BookOpenIcon,
  AlertTriangleIcon,
  SparklesIcon,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { cn } from '@/lib/utils';
import {
  useAcceptFinding,
  useClipStreamUrl,
  useCreateClip,
  useDeclineFinding,
  useVideoSource,
  useVideoStreamUrl,
} from '../../hooks/useVideo';
import { useCreateTask } from '../../hooks/useTask';
import type { Chapter, Finding, FindingCategory, FindingTargetType } from '../../services/video.service';

interface VideoToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseArgs(args: any, argsText: string): Record<string, any> {
  try {
    return typeof args === 'object' && args ? args : JSON.parse(argsText);
  } catch {
    return {};
  }
}

function parseResult(result?: string): any {
  if (result === undefined) return null;
  try {
    return typeof result === 'string' ? JSON.parse(result) : result;
  } catch {
    return null;
  }
}

function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00';
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatRange(start_ms: number, end_ms: number): string {
  return `${formatMs(start_ms)} – ${formatMs(end_ms)}`;
}

// --- ingest_video ----------------------------------------------------------

const IngestVideoResult: FC<{ data: any }> = ({ data }) => {
  // Poll the VideoSource so we surface "transcript_ready -> ready" transitions
  // without the user having to re-trigger the tool.
  const { data: video } = useVideoSource(data?.video_source_id);
  const live = video ?? data;
  const status = video?.status ?? data?.status;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <VideoIcon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{live?.title || 'Untitled video'}</span>
        <StatusBadge status={status} />
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          <ClockIcon className="inline h-3 w-3 mr-1" />
          {formatMs(live?.duration_ms ?? 0)}
        </span>
        <span>{(live?.transcript?.length ?? data?.segment_count) || 0} segments</span>
        <span>{live?.provider ?? data?.provider}</span>
      </div>
      {data?.transcript_preview?.length > 0 && (
        <div className="mt-2 space-y-1 ml-1">
          {data.transcript_preview.slice(0, 3).map((s: any, i: number) => (
            <div key={i} className="text-xs text-muted-foreground">
              <span className="font-mono mr-2">{formatMs(s.start_ms)}</span>
              {s.speaker && <span className="font-medium mr-1">{s.speaker}:</span>}
              <span>{s.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusBadge: FC<{ status?: string }> = ({ status }) => {
  if (!status) return null;
  const variant =
    status === 'ready' ? 'default' :
    status === 'failed' ? 'destructive' :
    'secondary';
  const label =
    status === 'transcript_ready' ? 'Transcript ready · video processing' :
    status === 'ready' ? 'Ready' :
    status === 'pending' ? 'Pending' :
    status === 'failed' ? 'Failed' :
    status;
  return <Badge variant={variant as any} className="text-[10px]">{label}</Badge>;
};

// --- get_video_transcript --------------------------------------------------

const GetTranscriptResult: FC<{ data: any }> = ({ data }) => {
  const count = data?.segments?.length ?? 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <FileTextIcon className="h-4 w-4 text-muted-foreground" />
      <span>Loaded transcript</span>
      <span className="text-xs text-muted-foreground">
        ({count} segments · {formatMs(data?.duration_ms ?? 0)})
      </span>
    </div>
  );
};

// --- search_video_transcript -----------------------------------------------

const SearchTranscriptResult: FC<{ args: any; data: any }> = ({ args, data }) => {
  const matches = data?.results ?? [];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <SearchIcon className="h-4 w-4 text-muted-foreground" />
        <span>
          {data?.match_count ?? 0} match{(data?.match_count ?? 0) === 1 ? '' : 'es'} for{' '}
          <span className="font-mono">"{args?.query}"</span>
        </span>
      </div>
      {matches.slice(0, 8).map((seg: any) => (
        <div
          key={seg.index}
          className={cn(
            'text-xs ml-2 pl-2 border-l-2',
            seg.is_match ? 'border-primary' : 'border-muted'
          )}
        >
          <span className="font-mono text-muted-foreground mr-2">
            {formatMs(seg.start_ms)}
          </span>
          {seg.speaker && <span className="font-medium mr-1">{seg.speaker}:</span>}
          <span className={seg.is_match ? 'text-foreground' : 'text-muted-foreground'}>
            {seg.text}
          </span>
        </div>
      ))}
      {matches.length > 8 && (
        <div className="text-xs text-muted-foreground ml-2">…and {matches.length - 8} more</div>
      )}
    </div>
  );
};

// --- summarize_video (async chapter generation + interactive chapter UI) ---

const SummarizeVideoResult: FC<{ data: any }> = ({ data }) => {
  const videoId = data?.video_source_id as string | undefined;
  const { data: video } = useVideoSource(videoId);

  const status = video?.chapters_status ?? data?.chapters_status;
  const chapters: Chapter[] = video?.chapters ?? [];

  if (status === 'generating' || (!chapters.length && status !== 'failed' && status !== 'ready')) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="h-4 w-4 animate-spin" />
        Generating chapter summary…
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <XCircleIcon className="h-4 w-4" />
        Chapter generation failed: {video?.chapters_status_detail || 'unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <ListIcon className="h-4 w-4 text-muted-foreground" />
        <span>{chapters.length} chapter{chapters.length === 1 ? '' : 's'}</span>
      </div>
      <ol className="space-y-1.5 ml-1">
        {chapters.map((c, i) => (
          <ChapterRow key={i} videoSourceId={videoId!} chapter={c} />
        ))}
      </ol>
    </div>
  );
};

const ChapterRow: FC<{ videoSourceId: string; chapter: Chapter }> = ({ videoSourceId, chapter }) => {
  const createClip = useCreateClip();
  const createCR = useCreateTask();
  const [createdClipId, setCreatedClipId] = useState<string | null>(null);
  const [showActionForm, setShowActionForm] = useState(false);
  const [actionDone, setActionDone] = useState(false);

  const onCreateClip = async () => {
    const clip = await createClip.mutateAsync({
      video_source_id: videoSourceId,
      start_ms: chapter.start_ms,
      end_ms: chapter.end_ms,
      title: chapter.title,
      summary: chapter.summary,
    });
    setCreatedClipId(clip.id);
  };

  return (
    <li className="text-xs border-l-2 border-muted pl-2 py-1">
      <div className="flex items-start gap-2">
        <span className="font-mono text-muted-foreground shrink-0 mt-0.5">
          {formatRange(chapter.start_ms, chapter.end_ms)}
        </span>
        <div className="flex-1">
          <div className="font-medium">{chapter.title}</div>
          {chapter.summary && <div className="text-muted-foreground mt-0.5">{chapter.summary}</div>}
          <div className="flex items-center gap-2 mt-1.5">
            {createdClipId ? (
              <Badge variant="secondary" className="text-[10px]">
                <PlayIcon className="h-3 w-3 mr-1" />
                Clip created
              </Badge>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[11px]"
                disabled={createClip.isPending}
                onClick={onCreateClip}
              >
                <ScissorsIcon className="h-3 w-3 mr-1" />
                {createClip.isPending ? 'Creating…' : 'Create clip'}
              </Button>
            )}
            {actionDone ? (
              <Badge variant="secondary" className="text-[10px]">
                <ClipboardCheckIcon className="h-3 w-3 mr-1" />
                Task created
              </Badge>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[11px]"
                onClick={() => setShowActionForm(true)}
              >
                <ClipboardCheckIcon className="h-3 w-3 mr-1" />
                Create task
              </Button>
            )}
          </div>
          {showActionForm && !actionDone && (
            <TaskForm
              defaultTitle={chapter.title}
              defaultDescription={chapter.summary || ''}
              attachedClipId={createdClipId}
              onCancel={() => setShowActionForm(false)}
              onSubmit={async ({ title, description, includeClip }) => {
                let clipId = createdClipId;
                if (includeClip && !clipId) {
                  const clip = await createClip.mutateAsync({
                    video_source_id: videoSourceId,
                    start_ms: chapter.start_ms,
                    end_ms: chapter.end_ms,
                    title: chapter.title,
                    summary: chapter.summary,
                  });
                  clipId = clip.id;
                  setCreatedClipId(clip.id);
                }
                await createCR.mutateAsync({
                  task_type: 'video_task',
                  title,
                  description,
                  context: JSON.stringify({
                    video_source_id: videoSourceId,
                    clip_id: clipId,
                    start_ms: chapter.start_ms,
                    end_ms: chapter.end_ms,
                  }),
                });
                setShowActionForm(false);
                setActionDone(true);
              }}
              submitting={createCR.isPending || createClip.isPending}
            />
          )}
        </div>
      </div>
    </li>
  );
};

const TaskForm: FC<{
  defaultTitle: string;
  defaultDescription: string;
  attachedClipId: string | null;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (v: { title: string; description: string; includeClip: boolean }) => void;
}> = ({ defaultTitle, defaultDescription, attachedClipId, submitting, onCancel, onSubmit }) => {
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [includeClip, setIncludeClip] = useState(true);

  return (
    <div className="mt-2 space-y-2 p-2 rounded border bg-background">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title"
        className="w-full text-xs px-2 py-1 border rounded bg-background"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What needs to be done?"
        rows={2}
        className="w-full text-xs px-2 py-1 border rounded bg-background resize-none"
      />
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={includeClip}
          onChange={(e) => setIncludeClip(e.target.checked)}
        />
        Attach video clip {attachedClipId ? '(already created)' : '(will be created)'}
      </label>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="default"
          className="h-6 px-2 text-[11px]"
          disabled={submitting || !title.trim()}
          onClick={() => onSubmit({ title, description, includeClip })}
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[11px]"
          disabled={submitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

// --- extract_key_findings (async findings + accept/decline) -----------------

const CATEGORY_LABEL: Record<FindingCategory, string> = {
  knowledge: 'Knowledge',
  gap: 'Data Gaps',
  task: 'Tasks',
};
const CATEGORY_ICON: Record<FindingCategory, FC<{ className?: string }>> = {
  knowledge: LightbulbIcon,
  gap: AlertTriangleIcon,
  task: ClipboardCheckIcon,
};
const CATEGORY_DEFAULT_TARGET: Record<FindingCategory, FindingTargetType> = {
  knowledge: 'knowledge_base_item',
  gap: 'semantic_gap',
  task: 'task',
};

const GAP_TYPES = [
  'missing_metric',
  'missing_dimension',
  'missing_relationship',
  'missing_source',
  'data_quality',
  'ambiguous_naming',
] as const;

const ExtractKeyFindingsResult: FC<{ data: any }> = ({ data }) => {
  const videoId = data?.video_source_id as string | undefined;
  const { data: video } = useVideoSource(videoId);

  const status = video?.findings_status ?? data?.findings_status;
  const findings: Finding[] = video?.findings ?? [];

  if (status === 'generating' || (!findings.length && status !== 'failed' && status !== 'ready')) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="h-4 w-4 animate-spin" />
        Extracting key findings…
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <XCircleIcon className="h-4 w-4" />
        Findings extraction failed: {video?.findings_status_detail || 'unknown error'}
      </div>
    );
  }

  if (!findings.length) {
    return (
      <div className="text-sm text-muted-foreground">
        No actionable findings extracted from this meeting.
      </div>
    );
  }

  const grouped: Record<FindingCategory, Finding[]> = { knowledge: [], gap: [], task: [] };
  for (const f of findings) {
    if (grouped[f.category]) grouped[f.category].push(f);
  }

  return (
    <div className="space-y-3">
      {(Object.keys(grouped) as FindingCategory[]).map((cat) => {
        const list = grouped[cat];
        if (!list.length) return null;
        const Icon = CATEGORY_ICON[cat];
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 text-sm font-bold text-foreground mb-1.5">
              <Icon className="h-4 w-4" />
              {CATEGORY_LABEL[cat]}
              <span className="text-xs font-normal text-muted-foreground/70">({list.length})</span>
            </div>
            <ol className="space-y-1.5 ml-1">
              {list.map((f) => (
                <FindingRow key={f.id} videoSourceId={videoId!} finding={f} />
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
};

const TimecodeButton: FC<{ start_ms: number; onClick: () => void; title?: string }> = ({
  start_ms,
  onClick,
  title,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title || 'Play from this point'}
    className="font-mono text-muted-foreground shrink-0 mt-0.5 hover:text-foreground hover:underline transition-colors cursor-pointer"
  >
    {formatMs(start_ms)}
  </button>
);

const FindingRow: FC<{ videoSourceId: string; finding: Finding }> = ({ videoSourceId, finding }) => {
  const accept = useAcceptFinding();
  const decline = useDeclineFinding();
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const openPreview = () => setPreviewOpen(true);

  if (finding.status === 'accepted') {
    return (
      <li className="text-xs border-l-2 border-success/40 pl-2 py-1 opacity-90">
        <div className="flex items-start gap-2">
          <TimecodeButton start_ms={finding.start_ms} onClick={openPreview} />
          <div className="flex-1">
            <div className="font-medium">{finding.title}</div>
            <div className="text-muted-foreground mt-0.5">
              <CheckCircle2Icon className="inline h-3 w-3 mr-1 text-success" />
              Accepted as {prettifyTarget(finding.created_object_type)}
              {finding.clip_id ? ' (clip attached)' : ''}
            </div>
          </div>
        </div>
        <VideoPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          videoSourceId={videoSourceId}
          startMs={finding.start_ms}
          endMs={finding.end_ms}
          title={finding.title}
        />
      </li>
    );
  }

  if (finding.status === 'declined') {
    return (
      <li className="text-xs border-l-2 border-muted pl-2 py-1 opacity-60">
        <div className="flex items-start gap-2">
          <TimecodeButton start_ms={finding.start_ms} onClick={openPreview} />
          <div className="flex-1">
            <div className="font-medium line-through">{finding.title}</div>
            <div className="text-muted-foreground mt-0.5">Declined</div>
          </div>
        </div>
        <VideoPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          videoSourceId={videoSourceId}
          startMs={finding.start_ms}
          endMs={finding.end_ms}
          title={finding.title}
        />
      </li>
    );
  }

  return (
    <li className="text-xs border-l-2 border-muted pl-2 py-1">
      <div className="flex items-start gap-2">
        <TimecodeButton start_ms={finding.start_ms} onClick={openPreview} />
        <div className="flex-1">
          <div className="font-medium">{finding.title}</div>
          {finding.summary && (
            <div className="text-muted-foreground mt-0.5">{finding.summary}</div>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[11px]"
              onClick={() => setShowAcceptForm(true)}
              disabled={accept.isPending}
            >
              <CheckCircle2Icon className="h-3 w-3 mr-1" />
              Accept
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] text-muted-foreground"
              onClick={() => decline.mutate({ videoSourceId, findingId: finding.id })}
              disabled={decline.isPending}
            >
              <XCircleIcon className="h-3 w-3 mr-1" />
              Decline
            </Button>
          </div>
          {showAcceptForm && (
            <FindingAcceptForm
              finding={finding}
              submitting={accept.isPending}
              onCancel={() => setShowAcceptForm(false)}
              onSubmit={async ({ targetType, payload, includeClip }) => {
                await accept.mutateAsync({
                  videoSourceId,
                  findingId: finding.id,
                  payload: { target_type: targetType, include_clip: includeClip, payload },
                });
                setShowAcceptForm(false);
              }}
            />
          )}
        </div>
      </div>
      <VideoPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        videoSourceId={videoSourceId}
        startMs={finding.start_ms}
        endMs={finding.end_ms}
        title={finding.title}
      />
    </li>
  );
};

const VideoPreviewDialog: FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  videoSourceId: string;
  startMs: number;
  endMs: number;
  title?: string;
}> = ({ open, onOpenChange, videoSourceId, startMs, endMs, title }) => {
  const { data: stream, isLoading, error } = useVideoStreamUrl(videoSourceId, { enabled: open });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hasSeeked, setHasSeeked] = useState(false);
  const url = stream && stream.ready ? stream.url : null;

  useEffect(() => {
    if (!open) setHasSeeked(false);
  }, [open]);

  const onLoadedMetadata = () => {
    const el = videoRef.current;
    if (el && !hasSeeked) {
      el.currentTime = startMs / 1000;
      setHasSeeked(true);
      // Autoplay from the seek point — users opening the preview almost
      // always want it to start playing immediately.
      void el.play().catch(() => { /* swallow autoplay rejections */ });
    }
  };

  const onTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.currentTime * 1000 >= endMs) {
      el.pause();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-4">
        <DialogTitle className="text-sm font-medium truncate">
          {title || 'Preview'}{' '}
          <span className="text-muted-foreground font-mono ml-2">
            {formatRange(startMs, endMs)}
          </span>
        </DialogTitle>
        {/* Reserve a 16:9 viewport before the video loads so the dialog
            opens at full height immediately instead of growing once metadata
            arrives. Loading + error states render inside the same box. */}
        <div className="mt-3 aspect-video w-full rounded border bg-black relative overflow-hidden">
          {!open ? null : isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="h-4 w-4 animate-spin" />
              Loading clip…
            </div>
          ) : error || !stream || stream.ready !== true ? (
            <div className="absolute inset-0 flex items-center justify-center p-4 text-sm text-muted-foreground text-center">
              Video isn't ready for playback yet.
              {stream && !stream.ready && stream.status_detail
                ? ` (${stream.status_detail})`
                : ''}
            </div>
          ) : (
            <video
              ref={videoRef}
              src={url || undefined}
              controls
              preload="metadata"
              onLoadedMetadata={onLoadedMetadata}
              onTimeUpdate={onTimeUpdate}
              className="absolute inset-0 w-full h-full"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FindingAcceptForm: FC<{
  finding: Finding;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (v: {
    targetType: FindingTargetType;
    payload: Record<string, any>;
    includeClip: boolean;
  }) => void;
}> = ({ finding, submitting, onCancel, onSubmit }) => {
  const [targetType, setTargetType] = useState<FindingTargetType>(
    CATEGORY_DEFAULT_TARGET[finding.category],
  );
  const [title, setTitle] = useState(finding.title);
  const [description, setDescription] = useState(finding.summary || '');
  const [gapType, setGapType] = useState<string>(finding.gap_type_hint || 'missing_source');
  const [includeClip, setIncludeClip] = useState(true);

  const submit = () => {
    let payload: Record<string, any> = {};
    if (targetType === 'knowledge_base_item') {
      payload = { name: title, body: description };
    } else if (targetType === 'semantic_gap') {
      payload = { title, description, gap_type: gapType };
    } else if (targetType === 'task') {
      payload = { title, description };
    }
    onSubmit({ targetType, payload, includeClip });
  };

  return (
    <div className="mt-2 space-y-2 p-2 rounded border bg-background">
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-muted-foreground w-16">Save as</label>
        <select
          value={targetType}
          onChange={(e) => setTargetType(e.target.value as FindingTargetType)}
          className="text-xs px-2 py-1 border rounded bg-background"
        >
          <option value="knowledge_base_item">Knowledge Base article</option>
          <option value="semantic_gap">Semantic gap</option>
          <option value="task">Task</option>
        </select>
      </div>
      {targetType === 'semantic_gap' && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-muted-foreground w-16">Gap type</label>
          <select
            value={gapType}
            onChange={(e) => setGapType(e.target.value)}
            className="text-xs px-2 py-1 border rounded bg-background"
          >
            {GAP_TYPES.map((g) => (
              <option key={g} value={g}>{g.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      )}
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={targetType === 'knowledge_base_item' ? 'Article name' : 'Title'}
        className="w-full text-xs px-2 py-1 border rounded bg-background"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={targetType === 'knowledge_base_item' ? 'Article body' : 'Description'}
        rows={3}
        className="w-full text-xs px-2 py-1 border rounded bg-background resize-none"
      />
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={includeClip}
          onChange={(e) => setIncludeClip(e.target.checked)}
        />
        Attach video clip ({formatRange(finding.start_ms, finding.end_ms)})
      </label>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="default"
          className="h-6 px-2 text-[11px]"
          disabled={submitting || !title.trim()}
          onClick={submit}
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[11px]"
          disabled={submitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

function prettifyTarget(t: string | null): string {
  if (!t) return 'record';
  if (t === 'knowledge_base_item') return 'a KB article';
  if (t === 'semantic_gap') return 'a semantic gap';
  if (t === 'task') return 'a task';
  return t;
}

// --- create_clip -----------------------------------------------------------

const CreateClipResult: FC<{ data: any }> = ({ data }) => {
  const clipId = data?.clip_id;
  const startMs = data?.start_ms ?? 0;
  const endMs = data?.end_ms ?? 0;
  const initiallyPlayable = data?.playable === true;

  // Poll the parent video so we know when the media flips to ready.
  const { data: video } = useVideoSource(data?.video_source_id);
  const videoReady = (video?.status ?? data?.video_status) === 'ready';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <ScissorsIcon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{data?.title}</span>
        <span className="text-xs text-muted-foreground">
          ({formatRange(startMs, endMs)})
        </span>
      </div>
      {videoReady || initiallyPlayable ? (
        <ClipPlayer clipId={clipId} startMs={startMs} endMs={endMs} />
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground ml-1">
          <Loader2Icon className="h-3 w-3 animate-spin" />
          Video still processing — clip will be playable when it's done.
        </div>
      )}
    </div>
  );
};

const ClipPlayer: FC<{ clipId: string; startMs: number; endMs: number }> = ({
  clipId,
  startMs,
  endMs,
}) => {
  const { data: stream, isLoading, error } = useClipStreamUrl(clipId);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hasSeeked, setHasSeeked] = useState(false);

  useEffect(() => {
    setHasSeeked(false);
  }, [stream && stream.ready ? stream.url : null]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground ml-1">
        <Loader2Icon className="h-3 w-3 animate-spin" />
        Loading clip…
      </div>
    );
  }

  if (error || !stream || stream.ready !== true) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground ml-1">
        <PlayIcon className="h-3 w-3" />
        Clip not playable yet.
      </div>
    );
  }

  const onLoadedMetadata = () => {
    const el = videoRef.current;
    if (el && !hasSeeked) {
      el.currentTime = startMs / 1000;
      setHasSeeked(true);
    }
  };

  const onTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.currentTime * 1000 >= endMs) {
      el.pause();
      el.currentTime = startMs / 1000;
    }
  };

  return (
    <video
      ref={videoRef}
      src={stream.url}
      controls
      preload="metadata"
      onLoadedMetadata={onLoadedMetadata}
      onTimeUpdate={onTimeUpdate}
      className="w-full max-w-xl rounded border bg-black"
    />
  );
};

// --- toolwide wrapper ------------------------------------------------------

const RUNNING_LABEL: Record<string, (a: Record<string, any>) => string> = {
  ingest_video: (a) => (a.url ? `Ingesting video from ${shortHost(a.url)}…` : 'Ingesting video…'),
  get_video_transcript: () => 'Loading full transcript…',
  search_video_transcript: (a) => (a.query ? `Searching transcript for "${a.query}"…` : 'Searching transcript…'),
  summarize_video: () => 'Starting chapter summary…',
  extract_key_findings: () => 'Starting findings extraction…',
  create_clip: (a) => (a.title ? `Creating clip "${a.title}"…` : 'Creating clip…'),
};

const COMPLETED_LABEL: Record<string, string> = {
  ingest_video: 'Video ingested',
  get_video_transcript: 'Transcript loaded',
  search_video_transcript: 'Transcript searched',
  summarize_video: 'Meeting summary',
  extract_key_findings: 'Key findings',
  create_clip: 'Clip created',
};

const ICON: Record<string, FC<{ className?: string }>> = {
  ingest_video: VideoIcon,
  get_video_transcript: FileTextIcon,
  search_video_transcript: SearchIcon,
  summarize_video: ListIcon,
  extract_key_findings: SparklesIcon,
  create_clip: ScissorsIcon,
};

function shortHost(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const VideoToolCallComponent: FC<VideoToolCallProps> = ({ toolName, args, argsText, result, status }) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = status?.type === 'complete' && !isError;

  const parsedArgs = parseArgs(args, argsText);
  const data = parseResult(result);
  const hasError = isError || data?.error;
  const Icon = ICON[toolName] || VideoIcon;

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning ? 'bg-primary/10 border-b border-primary/20' :
        hasError ? 'bg-destructive/10' :
        'bg-muted/50',
      )}>
        {isRunning ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0">{(RUNNING_LABEL[toolName] ?? (() => 'Running…'))(parsedArgs)}</p>
          </div>
        ) : hasError ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">{COMPLETED_LABEL[toolName] ?? toolName}</p>
            </div>
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive">{data?.error || 'Operation failed'}</p>
            </div>
          </div>
        ) : isComplete ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">{COMPLETED_LABEL[toolName] ?? toolName}</p>
              <CheckCircle2Icon className="h-3 w-3 text-success" />
            </div>
            {toolName === 'ingest_video' && <IngestVideoResult data={data} />}
            {toolName === 'get_video_transcript' && <GetTranscriptResult data={data} />}
            {toolName === 'search_video_transcript' && (
              <SearchTranscriptResult args={parsedArgs} data={data} />
            )}
            {toolName === 'summarize_video' && <SummarizeVideoResult data={data} />}
            {toolName === 'extract_key_findings' && <ExtractKeyFindingsResult data={data} />}
            {toolName === 'create_clip' && <CreateClipResult data={data} />}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0">{(RUNNING_LABEL[toolName] ?? (() => 'Running…'))(parsedArgs)}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export const VideoToolCall = VideoToolCallComponent as React.ComponentType<any>;
